import {Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
    CanvasService,
    ParametersService,
    StatisticsGainsService,
    StatisticsService,
    StatisticsTempService,
    SubtitlesService
} from "../core";
import {ChartMode, ICanvasJS, ICanvasJSStatic, Month, StatisticScope, TimePeriod } from "../models";
import {HttpClient} from "@angular/common/http";
import {Subscription} from "rxjs";
import {DOCUMENT} from "@angular/common";


@Component({
    selector: 'statistics',
    templateUrl: './statistics.component.html',
    styleUrls: ['./statistics.component.scss']
})
export class StatisticsComponent implements OnInit, OnDestroy {
    private canvasSubscription: Subscription;
    private statisticsChartSubscription: Subscription;
    private subtitlesSubscription: Subscription;
    loading: boolean = true;
    months: Month[] = [];
    years: string[] = [];
    chart: ICanvasJS;

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private canvas: CanvasService,
        private http: HttpClient,
        private parameters: ParametersService,
        public statistics: StatisticsService,
        private statisticsGains: StatisticsGainsService,
        public statisticsTemp: StatisticsTempService,
        private subtitles: SubtitlesService,
    ) {
        this.statistics.state = 'temperatures';
        this.statistics.timePeriod = 'day';
        this.makeMonths();
        this.makeYears();
    }

    ngOnInit() {
        this.subtitlesSubscription = this.subtitles.subtitlesChanged$.subscribe(() => {
            this.makeMonths();
        });

        this.statisticsChartSubscription = this.statistics.statisticsChartReady$.subscribe((tempChartObject) => {
            if (this.statistics.messageContent === '' && tempChartObject === null) {
                return;
            }
            if (!tempChartObject) {
                return;
            }

            this.chart = new (window as unknown as { CanvasJS: ICanvasJSStatic }).CanvasJS.Chart(
                "chartContainer", tempChartObject
            );
            this.chart.render();
            this.loading = false;
        });

        this.canvasSubscription = this.canvas.canvasJSLoaded$.subscribe((loaded: boolean) => {
            if(loaded) {
                this.generateChart();
            }
        });

        if (!this.canvas.canvasJSLoaded.getValue()) {
            this.canvas.loadCanvasJS();
        }
    }

    ngOnDestroy() {
        this.canvasSubscription.unsubscribe();
        this.statisticsChartSubscription.unsubscribe();
        this.subtitlesSubscription.unsubscribe();
        this.statistics.statisticsChartReady.next(null);
        this.statistics.state = 'temperatures';
    }

    subtitle(id: number) {
        return this.subtitles.setSubtitle(id);
    }

    private makeMonths(): void {
        this.months = [];
        for (let i = 1; i < 13; i++) {
            this.months.push({
                index: i,
                value: this.subtitle(1009 + i)
            });
        }
    }

    private makeYears(): void {
        const t = new Date();
        this.years = [];
        for (let i = t.getFullYear(); i>=2015; i--) {
            this.years.push( i.toString() );
        }
    }

    isStatisticsScope(scope: keyof StatisticScope): boolean {
        if(this.statistics.isYieldStateGuard(this.statistics.state)){
            return this.statisticsTemp.columnsOccurrence[this.statistics.state][scope];
        }
        if(this.statistics.isDynamicYieldsGuard(this.statistics.state)){
            return this.statistics.state[scope];
        }
        return false;
    }

    onChartModeChange(mode: ChartMode): void {
        this.statistics.rerenderChart(mode);
    }

    generateChart() {
        this.loading = true;

        if (this.statistics.isDynamicYields()) {
            this.statisticsGains.getDynamicYieldsStatistics();
        } else if (this.statistics.state === 'temperatures') {
            this.statisticsTemp.initialize(
                this.statistics.timePeriod,
                this.statistics.selectedMonth,
                this.statistics.selectedYear
            );
        } else {
            this.statisticsGains.getYieldStatisticsData();
        }
        this.statistics.currentGeneratedType = this.statistics.state;
    }

    stateToggle() {
        if (this.statistics.isDynamicYields()) {
            this.statistics.setTimePeriodForDynamicYields();
            return;
        }
        if (this.statistics.isYieldStateGuard(this.statistics.state)) {
            for (const property in this.statisticsTemp.columnsOccurrence[this.statistics.state]) {
                if (this.statisticsTemp.columnsOccurrence[this.statistics.state][property] === true) {
                    this.statistics.timePeriod = property as TimePeriod;
                    return;
                }
            }
        }
        this.statistics.timePeriod = 'day';
    }

    private dateToCsv() {
        let currentDate = new Date();

        return (((currentDate.getDate()) < 10) ? '0' : '')
            + currentDate.getDate() + "-"
            + (((currentDate.getMonth()+1) < 10) ? '0' : '')
            + (currentDate.getMonth()+1)  + "-"
            + currentDate.getFullYear() + " "
            + ((currentDate.getHours()<10) ? '0' : '')
            + currentDate.getHours() + ":"
            + ((currentDate.getMinutes()<10) ? '0' : '')
            + currentDate.getMinutes();
    }

    downloadCsv() {
        let csvContent = ''
        this.loading = true;

        let statisticsType = 'linear';
        if (this.statistics.isYieldStateGuard(this.statistics.state)) {
            statisticsType = this.statistics.state;
        }
        if (this.statistics.isDynamicYieldsGuard(this.statistics.state)) {
            statisticsType = this.statistics.state.objectId.toString();
        }

        const baseUrl = `/api/v1/modules/${this.parameters.selectedModuleHash}`;
        const { timePeriod, selectedMonth, selectedYear } = this.statistics;
        const range = timePeriod === 'month'
            ? `range/month/${selectedMonth}/year/${selectedYear}`
            : `range/${timePeriod}`;
        const languageAndTag = `/language/${this.subtitles.language}${this.subtitles.getTagParams()}`;
        const url = `${baseUrl}/statistics/${statisticsType}/${range}${languageAndTag}`;

        this.http.options(url, { responseType: 'text' }).subscribe((response) => {
            csvContent += response
            this.checkPlatformBeforeDownload(csvContent);
        });
    }

    private checkPlatformBeforeDownload(csvContent: string): void {
        const today = this.dateToCsv();
        const platform = this.document.getElementById('platform').innerHTML;

        if (platform === 'android') {
            const { blob, fileName } = this.createBlobAndFilename(csvContent, today);
            window.localStorage.setItem('file_content', btoa((blob as any) as string));
            window.localStorage.setItem('file_name', fileName);
            window.localStorage.setItem('file_type', 'csv');
            this.http.get('/api/v1/download_android').subscribe();
            this.loading = false;
        } else {
            this.downloadFile(csvContent, today);
        }
    }

    private createBlobAndFilename(csvContent: string, today: string): { blob: Blob, fileName: string } {
        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/plain;charset=utf-8' });
        const fileName = `${this.parameters.modules[this.parameters.selectedModuleIndex].name}_${today}.csv`;

        return { blob, fileName };
    }

    private downloadFile(csvContent: string, today: string): void {
        const linkElement = document.createElement('a');
        const { blob, fileName } = this.createBlobAndFilename(csvContent, today);

        try {
            const url = window.URL.createObjectURL(blob);
            linkElement.setAttribute('href', url);
            linkElement.setAttribute("download", fileName);

            const clickEvent = new MouseEvent("click", {
                view: window,
                bubbles: true,
                cancelable: false
            });

            if(window.navigator.userAgent.indexOf("Edge") > -1) {
                // @ts-ignore
                window.navigator.msSaveOrOpenBlob(blob,fileName);
            } else {
                linkElement.dispatchEvent(clickEvent);
            }
        } catch (ex) {
            // @ts-ignore
            window.navigator.msSaveBlob(blob,fileName);
        }
        this.loading = false;
    }

    showHideAllCharts(flag: boolean): void {
        const thickness = (flag ? 2 : 0);

        this.chart.data.forEach((series) => {
            series.set('visible', flag);

            const axes = series.axisYType === 'secondary' ? this.chart.axisY2 : this.chart.axisY;
            const axis = axes[Number(series.axisYIndex)];
            if (!axis) {
                return;
            }

            axis.set('crosshair', {
                labelMaxWidth: 100,
                labelWrap: false,
                enabled: flag,
                color: "red",
                thickness: 1,
                lineDashType: "solid",
                labelBackgroundColor: this.statistics.basicColor(),
                labelFontColor: this.statistics.oppositeColor(),
            });
            axis.set('lineThickness', thickness);
            axis.set('tickThickness', thickness);
        });
    }
}
